import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ScrubInput } from 'calc-controls';
import { useAlert } from '@ascend/alert';
import { useStore } from '@/store';
import { selectStorageUsed } from '@/store/selectors';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useAppLock } from '@/components/AppLock';
import { clearPersistedState } from '@/persistence/lockStorageBridge';
import { openAbout } from '@/utils/aboutBus';
import {
  TrapezoidalRule as TR,
  BlqRule as BR,
  AucInterval as AI,
} from '@bioavail/shared-types';
import type { AppSettings } from '@/types/persistence';

/**
 * Settings screen — appearance, defaults, and privacy/architecture info.
 */
export function SettingsScreen() {
  return (
    <div className="settings-screen">
      <AppearanceSection />
      <DefaultsSection />
      <DemoDataSection />
      <AppLockSection />
      <PrivacySection />
      <hr className="divider" />
      <AboutSection />
    </div>
  );
}

function AboutSection() {
  return (
    <div className="settings-about">
      <Button variant="secondary" size="sm" onClick={() => openAbout()}>
        About
      </Button>
    </div>
  );
}

function AppearanceSection() {
  const settings = useStore((s) => s.settings);
  const setTheme = useStore((s) => s.setTheme);

  return (
    <CollapsibleSection sectionKey="settings.appearance" title="Appearance" column="single" defaultOpen>
      <Select
        label="Theme"
        value={settings.theme}
        onChange={(v) => setTheme(v as AppSettings['theme'])}
        options={[
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
          { value: 'system', label: 'Follow system' },
        ]}
      />
    </CollapsibleSection>
  );
}

function DefaultsSection() {
  const settings = useStore((s) => s.settings);
  const updateSettings = useStore((s) => s.updateSettings);

  return (
    <CollapsibleSection sectionKey="settings.defaults" title="Defaults & Layout" column="single">
      <div className="flex-col-gap-3">
        <Select
          label="Default trapezoidal rule"
          value={settings.defaultTrapezoidalRule}
          onChange={(v) => updateSettings({ defaultTrapezoidalRule: v as TR })}
          options={[
            { value: TR.LinearUpLogDown, label: 'Linear-up / log-down' },
            { value: TR.LinearTrapezoidal, label: 'Linear trapezoidal' },
            { value: TR.LinearLog, label: 'Linear / log' },
          ]}
          hint="Applied to new arms. Linear-up / log-down is the regulatory standard."
        />

        <Select
          label="Default BLQ rule"
          value={settings.defaultBlqRule}
          onChange={(v) => updateSettings({ defaultBlqRule: v as BR })}
          options={[
            { value: BR.M1, label: 'M1 — treat BLQ as 0' },
            { value: BR.M2, label: 'M2 — treat BLQ as LLOQ/2' },
          ]}
          hint="M1 zeroes below-limit-of-quantification samples; M2 substitutes LLOQ/2."
        />

        <Select
          label="Default AUC interval"
          value={settings.defaultAucInterval}
          onChange={(v) => updateSettings({ defaultAucInterval: v as AI })}
          options={[
            { value: AI.ZeroToT, label: 'AUC(0-t)' },
            { value: AI.ZeroToInf, label: 'AUC(0-∞)' },
          ]}
          hint="AUC(0-t) uses observed data only; AUC(0-∞) extrapolates the terminal tail."
        />

        <div className="number-input-wrapper">
          <label className="field-label">Significant figures</label>
          <ScrubInput
            label="Significant figures"
            value={settings.significantFigures}
            onChange={(v) => updateSettings({ significantFigures: v })}
            min={2}
            max={8}
            step={1}
            decimals={0}
            inline
          />
          <p className="field-hint">Decimal places used when displaying all computed results.</p>
        </div>
      </div>
    </CollapsibleSection>
  );
}

function DemoDataSection() {
  const settings = useStore((s) => s.settings);
  const updateSettings = useStore((s) => s.updateSettings);
  const clearDemoData = useStore((s) => s.clearDemoData);
  const pushToast = useStore((s) => s.pushToast);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <CollapsibleSection sectionKey="settings.demos" title="Demo Data" column="single">
      <div className="flex-col-gap-3">
        <div className="info-box">
          <h4>Demo datasets</h4>
          <p className="field-hint">
            Use the "Load Demo" button in the top bar to load any of {6} pre-configured
            study snapshots covering absolute F, relative F, partial AUC, and direct-AUC paths.
          </p>
        </div>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={settings.showLoadDemo}
            onChange={(e) => updateSettings({ showLoadDemo: e.target.checked })}
          />
          <span>Show "Load Demo" button in the top bar</span>
        </label>

        <Button variant="danger" size="sm" onClick={() => setConfirmOpen(true)}>
          Clear demo data
        </Button>

        <Modal
          open={confirmOpen}
          title="Clear all study data?"
          onClose={() => setConfirmOpen(false)}
          footer={
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setConfirmOpen(false);
                  clearDemoData();
                  pushToast('Study data cleared.', 'info');
                }}
              >
                Clear data
              </Button>
            </>
          }
        >
          <p>
            This removes all loaded studies and resets the calculator to a fresh default study.
            This action cannot be undone.
          </p>
        </Modal>
      </div>
    </CollapsibleSection>
  );
}

function AppLockSection() {
  const { isEnabled, isConfigured, lock, enableEncryption, skipEncryption } = useAppLock();
  const { prompt, alert } = useAlert();

  return (
    <CollapsibleSection sectionKey="settings.applock" title="App Lock" column="single">
      <div className="flex-col-gap-3">
        <div className="info-box">
          <h4>Data encryption</h4>
          <p className="field-hint">
            {isEnabled
              ? 'Your study data is encrypted with AES-256-GCM. A password is required to unlock the app.'
              : 'Enable password protection to encrypt your study data with AES-256-GCM.'}
          </p>
        </div>

        <div className="flex-row-gap-2-wrap">
          {isEnabled && lock ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => lock.lock('manual')}
              >
                Lock now
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                  const pw = await prompt({
                    title: 'Remove encryption',
                    label: 'Enter your password to remove encryption:',
                    inputType: 'password',
                    confirmLabel: 'Remove',
                  });
                  if (!pw) return;
                  try {
                    await lock.removePassword(pw);
                  } catch {
                    await alert({
                      title: 'Incorrect password',
                      message: 'The password you entered is incorrect.',
                      type: 'danger',
                    });
                  }
                }}
              >
                Remove password
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" size="sm" onClick={enableEncryption}>
                Enable password protection
              </Button>
              {(
                <Button variant="ghost" size="sm" onClick={skipEncryption}>
                  Keep unencrypted
                </Button>
              )}
            </>
          )}
        </div>

        {isEnabled && isConfigured ? (
          <p className="field-hint">
            Encryption is active. Use the lock icon in the top bar to change your
            password or lock the app immediately.
          </p>
        ) : null}
      </div>
    </CollapsibleSection>
  );
}

function PrivacySection() {
  const storageUsed = useStore(useShallow(selectStorageUsed));
  const settings = useStore((s) => s.settings);
  const updateSettings = useStore((s) => s.updateSettings);
  const pushToast = useStore((s) => s.pushToast);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <CollapsibleSection sectionKey="settings.privacy" title="Privacy & Architecture" column="single">
      <div className="flex-col-gap-3">
        <div className="info-box">
          <h4>Client-side execution</h4>
          <p className="field-hint">
            All calculations run entirely in your browser. No data is transmitted to any server.
            Study inputs and results persist only in your browser's local storage.
          </p>
        </div>

        <div className="info-box">
          <h4>PWA</h4>
          <p className="field-hint">
            This app is installable as a Progressive Web App. Use your browser's
            "Install" or "Add to Home Screen" option to use it offline.
          </p>
        </div>

        <div className="storage-meter">
          <span className="field-label">
            Storage: {formatBytes(storageUsed.used)} / {formatBytes(storageUsed.quota)}
          </span>
          <div className="storage-bar">
            <div className="storage-fill" style={{ transform: `scaleX(${Math.min(storageUsed.percent, 100) / 100})` }} />
          </div>
        </div>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={settings.autoSave}
            onChange={(e) => updateSettings({ autoSave: e.target.checked })}
          />
          <span>Auto-save study data</span>
        </label>

        <Button variant="danger" size="sm" onClick={() => setConfirmOpen(true)}>
          Clear all saved data
        </Button>

        <Modal
          open={confirmOpen}
          title="Clear all saved data?"
          onClose={() => setConfirmOpen(false)}
          footer={
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setConfirmOpen(false);
                  void clearPersistedState().then(() => {
                    pushToast('All data cleared. Reloading…', 'info');
                    setTimeout(() => window.location.reload(), 1000);
                  });
                }}
              >
                Clear data
              </Button>
            </>
          }
        >
          <p>
            This will permanently remove all saved studies, presets, and settings from your browser.
            This action cannot be undone.
          </p>
        </Modal>
      </div>
    </CollapsibleSection>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
